import { useState, useEffect, useCallback, useRef } from "react";

export interface PopupItem {
  id: number;
  text: string;
  icon: string | undefined;
  duration: number;
  header?: string;
}

interface PopupNotificationProps {
  queue: PopupItem[];
  onDismiss: (id: number) => void;
}

export function PopupNotification({ queue, onDismiss }: PopupNotificationProps) {
  const current = queue[0];
  if (!current) return null;

  return <PopupToast key={current.id} item={current} onDismiss={onDismiss} />;
}

function PopupToast({ item, onDismiss }: { item: PopupItem; onDismiss: (id: number) => void }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onDismiss(item.id);
    }, 300);
  }, [item.id, onDismiss]);

  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 16);
    const autoTimer = setTimeout(dismiss, item.duration * 1000);
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(autoTimer);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [item.duration, dismiss]);

  return (
    <div
      style={{
        position: "absolute",
        top: "2rem",
        left: "50%",
        transform: `translateX(-50%) translateY(${visible && !exiting ? "0" : "-1rem"})`,
        opacity: visible && !exiting ? 1 : 0,
        transition: "opacity 0.3s ease, transform 0.3s ease",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.6rem 1.2rem",
        background: "rgba(0, 0, 0, 0.75)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "8px",
        color: "#fff",
        fontSize: "0.95rem",
        zIndex: 150,
        pointerEvents: "auto",
        cursor: "pointer",
        maxWidth: "80%",
      }}
      onClick={dismiss}
    >
      {item.icon && (
        <img
          src={item.icon}
          alt=""
          style={{ width: "28px", height: "28px", objectFit: "contain", flexShrink: 0 }}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {item.header && (
          <span style={{ fontSize: "0.7rem", opacity: 0.65, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--theme-accent, #7ec8e3)" }}>
            {item.header}
          </span>
        )}
        <span>{item.text}</span>
      </div>
    </div>
  );
}
